import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useIsMobile } from "../../hooks/useIsMobile";
import "./Authentication.css";
import AuthForgotPasswordFlow from "../../components/Authentication/AuthForgotPasswordFlow";
import AuthenticationService from "../../services/AuthenticationService";
import { validateEmailInput, parseForgotPasswordError } from "../../utils/forgotPasswordErrorHandler";
import { validateResetPasswordInput, parseResetPasswordError } from "../../utils/resetPasswordErrorHandler";
import AuthLoginForm from "../../components/Authentication/AuthLoginForm";
import { parseLoginError, validateLoginInput } from "../../utils/loginErrorHandler";
import { AuthContext } from "../../context/AuthContext";
import envelopeIcon from "../../assets/icons/auth/forms/envelope.png";
import lockIcon from "../../assets/icons/auth/forms/lock.png";
import hashtag from "../../assets/icons/auth/forms/lock-hashtag.png";
import eye from "../../assets/icons/auth/forms/eye.png";
import tickIcon from "../../assets/icons/status/Tick.png";
import crossIcon from "../../assets/icons/status/Cross.png";
import envelopeResetIcon from "../../assets/icons/status/Envelope.png";
import eyeOff from "../../assets/icons/auth/forms/eye-crossed.png";
import SecondButton from "../../components/Elements/SecondButton/SecondButton";

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const navigate = useNavigate();
  const isMobile = useIsMobile(770);
  const { setToken, setFirebaseToken } = useContext(AuthContext);

  const [forgotPasswordStage, setForgotPasswordStage] = useState("email");
  const [resetStatus, setResetStatus] = useState("sending");
  const [passwordResetStatus, setPasswordResetStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [showLoginAfterReset, setShowLoginAfterReset] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleSendResetEmail = async () => {
    setError("");
    const validationError = validateEmailInput(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await AuthenticationService.sendResetCode(email);
      setForgotPasswordStage("reset");
      setResetStatus("sent");
      setPasswordResetStatus("emailSent");
    } catch (err) {
      const message = parseForgotPasswordError(err);
      setError(message);
      setResetStatus("error");
    }
  };

  const handleResetPassword = async () => {
    setError("");
    const validationError = validateResetPasswordInput(email, resetCode, newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await AuthenticationService.resetPassword(email, resetCode, newPassword);
      setForgotPasswordStage("email");
      setResetCode("");
      setNewPassword("");
      setResetStatus("sending");

      setShowLoginAfterReset(true);
      setPasswordResetStatus("success");
    } catch (err) {
      const message = parseResetPasswordError(err);
      setError(message);
      setPasswordResetStatus("error");
      setSuccessMessage(null);
    }
  };

  const handleLoginAfterReset = async (loginEmail, loginPassword) => {
    const validationError = validateLoginInput(loginEmail, loginPassword);
    if (validationError) {
      setLoginError(validationError);
      return;
    }
    try {
      const response = await AuthenticationService.loginUser(loginEmail, loginPassword);
      setToken(response.access_token);
      setFirebaseToken(response.firebase_token);
      localStorage.setItem("token", response.access_token);
      localStorage.setItem("firebaseToken", response.firebase_token);
      if (rememberMe) {
        localStorage.setItem("Email", loginEmail);
        localStorage.setItem("Password", loginPassword);
      }
      navigate(`/${lang}/shop`);
    } catch (err) {
      const message = parseLoginError(err.response);
      setLoginError(message);
    }
  };

  const handleBackToLogin = () => {
    navigate(`/${lang}/sign-in`);
  };

  useEffect(() => {
    setPasswordResetStatus(null);
    setError(null);
  }, []);

  return (
    <>
      <Helmet>
        <title>Telviora - {t("auth.forgotPasswordTitle", "Forgot Password?")}</title>
      </Helmet>
      <div className="auth-container">
        <div className="welcome-section">
          {passwordResetStatus === "success" ? (
            <>
              <div className="status-header">
                <img src={tickIcon} alt="Success" className="welcome-status-icon" />
                <h1>{t("auth.successHeader", "SUCCESS!")}</h1>
              </div>
              <p className="description">
                {t(
                  "auth.forgotPasswordSuccessLead",
                  "Your password has been successfully changed. You can now log in with your new credentials.",
                )}
              </p>
            </>
          ) : passwordResetStatus === "error" ? (
            <>
              <div className="status-header">
                <img src={crossIcon} alt="Error" className="welcome-status-icon" />
                <h1>{t("auth.errorHeader", "OOOPS!")}</h1>
              </div>
              <p className="description">
                {t(
                  "auth.registerErrorLead",
                  "Something went wrong. Please check your verification code and try again.",
                )}
              </p>
            </>
          ) : passwordResetStatus === "emailSent" ? (
            <>
              <div className="status-header">
                <img src={envelopeResetIcon} alt="Envelope" className="welcome-status-icon" />
                <h1>{t("auth.forgotPasswordSentTitle", "Password Reset!")}</h1>
              </div>
              <p className="description">
                {t(
                  "auth.forgotPasswordSentLead",
                  "Check your inbox — your password reset code is waiting! Enter it in the field on the right to reset your password.",
                )}
              </p>
            </>
          ) : (
            <>
              <h1>{t("auth.forgotPasswordTitle", "Forgot Password?")}</h1>
              <p className="description">
                {t("auth.forgotPasswordLead", "Reset your password using email 2FA authentication.")}
              </p>
              <SecondButton className="toggle-register-desktop" onClick={handleBackToLogin}>
                {t("auth.signIn", "Sign In")}
              </SecondButton>
            </>
          )}
        </div>

        <div className="sign-in-section">
          {showLoginAfterReset ? (
            <AuthLoginForm
              envelopeIcon={envelopeIcon}
              lockIcon={lockIcon}
              eye={eye}
              eyeOff={eyeOff}
              email={email}
              password={newPassword || ""}
              showPassword={showPassword}
              error={loginError}
              isMobile={isMobile}
              onEmailChange={setEmail}
              onPasswordChange={setNewPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
              onLogin={handleLoginAfterReset}
              rememberMe={rememberMe}
              onRememberChange={setRememberMe}
              onSwitchToRegister={() => {}}
              successMessage={successMessage}
              setSuccessMessage={setSuccessMessage}
              onCloseError={() => setLoginError(null)}
              onForgotPassword={() => {}}
            />
          ) : (
            <AuthForgotPasswordFlow
              forgotPasswordStage={forgotPasswordStage}
              resetStatus={resetStatus}
              envelopeIcon={envelopeIcon}
              lockIcon={lockIcon}
              hashtag={hashtag}
              eye={eye}
              eyeOff={eyeOff}
              email={email}
              resetCode={resetCode}
              newPassword={newPassword}
              showPassword={showPassword}
              error={error}
              onEmailChange={setEmail}
              onResetCodeChange={setResetCode}
              onNewPasswordChange={setNewPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
              onSendResetEmail={handleSendResetEmail}
              onResetPassword={handleResetPassword}
              onBackToLogin={handleBackToLogin}
              onCloseError={() => setError(null)}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
